/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils";

type Side = "top" | "bottom" | "left" | "right";
type Align = "start" | "center" | "end";

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: Side;
  align?: Align;
  delayMs?: number;
  className?: string;
}

const GAP = 8;
const EDGE = 8;
const ARROW_CLAMP = 12;
const ARROW_HALF = 3;

const FLIP: Record<Side, Side> = {
  top: "bottom",
  bottom: "top",
  left: "right",
  right: "left",
};

function resolve(
  tr: DOMRect,
  tt: DOMRect,
  side: Side,
  align: Align,
  vw: number,
  vh: number,
) {
  const vert = side === "top" || side === "bottom";

  let t =
    side === "top"
      ? tr.top - tt.height - GAP
      : side === "bottom"
        ? tr.bottom + GAP
        : align === "start"
          ? tr.top
          : align === "end"
            ? tr.bottom - tt.height
            : tr.top + (tr.height - tt.height) / 2;

  let l =
    side === "left"
      ? tr.left - tt.width - GAP
      : side === "right"
        ? tr.right + GAP
        : align === "start"
          ? tr.left
          : align === "end"
            ? tr.right - tt.width
            : tr.left + (tr.width - tt.width) / 2;

  const fits = vert
    ? side === "top"
      ? t >= EDGE
      : t + tt.height <= vh - EDGE
    : side === "left"
      ? l >= EDGE
      : l + tt.width <= vw - EDGE;

  l = Math.max(EDGE, Math.min(l, vw - tt.width - EDGE));
  t = Math.max(EDGE, Math.min(t, vh - tt.height - EDGE));

  return { top: t, left: l, fits };
}

export function Tooltip({
  content,
  children,
  side = "top",
  align = "center",
  delayMs = 400,
  className,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: -9999, left: -9999 });
  const [arrow, setArrow] = useState(0);
  const [activeSide, setActiveSide] = useState(side);

  const triggerRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const canHover = useRef(
    typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(hover: hover)").matches,
  );

  const place = useCallback(() => {
    const trigger = triggerRef.current;
    const tip = tipRef.current;
    if (!trigger || !tip) return;

    const tr = trigger.getBoundingClientRect();
    const tt = tip.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let result = resolve(tr, tt, side, align, vw, vh);
    let used = side;

    if (!result.fits) {
      const flipped = resolve(tr, tt, FLIP[side], align, vw, vh);
      if (flipped.fits) {
        result = flipped;
        used = FLIP[side];
      }
    }

    setPos({ top: result.top, left: result.left });
    setActiveSide(used);

    const isVert = used === "top" || used === "bottom";
    const center = isVert
      ? tr.left + tr.width / 2 - result.left
      : tr.top + tr.height / 2 - result.top;
    const max = isVert ? tt.width : tt.height;
    setArrow(Math.max(ARROW_CLAMP, Math.min(center, max - ARROW_CLAMP)));
  }, [side, align]);

  const show = useCallback(() => {
    if (!canHover.current) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(true), delayMs);
  }, [delayMs]);

  const hide = useCallback(() => {
    clearTimeout(timer.current);
    setOpen(false);
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  useEffect(() => () => clearTimeout(timer.current), []);

  if (!content) return <>{children}</>;

  const isVert = activeSide === "top" || activeSide === "bottom";

  const arrowStyle: React.CSSProperties = {
    ...(isVert
      ? { left: arrow, transform: "translateX(-50%) rotate(45deg)" }
      : { top: arrow, transform: "translateY(-50%) rotate(45deg)" }),
    ...(activeSide === "top" && { bottom: -ARROW_HALF }),
    ...(activeSide === "bottom" && { top: -ARROW_HALF }),
    ...(activeSide === "left" && { right: -ARROW_HALF }),
    ...(activeSide === "right" && { left: -ARROW_HALF }),
  };

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className="inline-flex max-w-full"
      >
        {children}
      </span>

      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={tipRef}
            role="tooltip"
            style={
              {
                top: pos.top,
                left: pos.left,
                "--tt-x": isVert ? "0px" : activeSide === "right" ? "-4px" : "4px",
                "--tt-y": !isVert ? "0px" : activeSide === "bottom" ? "-4px" : "4px",
              } as React.CSSProperties
            }
            className={cn(
              "fixed z-50 px-3 py-1.5",
              "text-xs font-medium leading-snug whitespace-nowrap",
              "rounded-lg pointer-events-none select-none",
              "tooltip-surface",
              "animate-tooltip-in",
              className,
            )}
          >
            {content}
            <span
              className="absolute h-[6px] w-[6px] rounded-[1px] tooltip-surface"
              style={arrowStyle}
            />
          </div>,
          document.body,
        )}
    </>
  );
}
